import { translate } from "../components/IntlProviderWrapper";
import { getBackend } from "./backend/Backend2";
import { getLineGroupForUser } from "./BusinessLogic";

import type { ILineGroupGroup, IShiftPersonLineRec } from "./backend/manufacturing2.generated.types";

/**
 * Generic line data interface for LineLayoutEditor.
 * Any line data passed to the editor must conform to this interface.
 */

export type DraggableItem = {
    id: string;
};

export type LineData = DraggableItem & {
    title: string;
};

export type LineSection = DraggableItem & {
    title: string;
    lines: SectionLine[];
};

export type SectionLine = DraggableItem & {
    showDividerAfter: boolean;
    title: string;
};

export type SectionHeader = DraggableItem & {
    title: string;
};

export type SectionedLine<T> = {
    sectionHeader: SectionHeader | null;
    line: T;
    showDividerAfter: boolean;
};

export const SECTION_ID_PREFIX = "SECTION-";
export const LINE_ID_PREFIX = "LINE-";

export function toSectionId(numericId: number): string {
    return `${SECTION_ID_PREFIX}${numericId}`;
}

export function toLineId(numericId: number): string {
    return `${LINE_ID_PREFIX}${numericId}`;
}

export function parseSectionId(sectionId: string): number {
    return Number(sectionId.slice(SECTION_ID_PREFIX.length));
}

export function parseLineId(lineId: string): number {
    return Number(lineId.slice(LINE_ID_PREFIX.length));
}

export function isTempSectionId(sectionId: string): boolean {
    return parseSectionId(sectionId) < 0;
}

export const DEFAULT_LINE_SECTION_ID = toSectionId(0);

const getDefaultLineSectionTitle = (): string => translate("common.other", "Other");

/**
 * Creates the default section containing all lines that are not assigned to any section.
 *
 * @param sections - The current list of sections
 * @param lines - All available lines
 * @returns The default section with unassigned lines
 */
export function createDefaultLineSection(sections: LineSection[], lines: IShiftPersonLineRec[]): LineSection {
    // Collect all assigned line IDs
    const assignedIds = new Set<string>();
    for (const section of sections) {
        for (const line of section.lines) {
            assignedIds.add(line.id);
        }
    }

    // Find unassigned lines
    const unassignedLines: SectionLine[] = lines
        .filter(line => !assignedIds.has(toLineId(line.line_id)))
        .map(line => ({ id: toLineId(line.line_id), title: line.line_title, showDividerAfter: false }));

    return {
        id: DEFAULT_LINE_SECTION_ID,
        title: getDefaultLineSectionTitle(),
        lines: unassignedLines,
    };
}

/**
 * Moves a line from one section to another.
 *
 * @param sections - The current list of sections
 * @param sourceSectionId - The ID of the section to remove the line from
 * @param targetSectionId - The ID of the section to add the line to
 * @param line - The line to move (uses line.id to identify which line to remove)
 * @returns The updated sections array with the line moved
 */
export function moveLineBetweenSections(
    sections: LineSection[],
    sourceSectionId: string,
    targetSectionId: string,
    line: SectionLine
): LineSection[] {
    return sections.map(section => {
        if (section.id === sourceSectionId) {
            // Remove line from source section
            return {
                ...section,
                lines: section.lines.filter(l => l.id !== line.id),
            };
        } else if (section.id === targetSectionId) {
            // Add line to target section at the end
            return {
                ...section,
                lines: [...section.lines, line],
            };
        }
        return section;
    });
}

/**
 * Updates the lines in a specific section.
 * @param sections - The current list of sections
 * @param lines - The lines to update
 * @param selectedSectionId - The ID of the section to update
 * @returns The updated sections array with the lines updated
 */
export function updateLinesInSection(
    sections: LineSection[],
    lines: SectionLine[],
    selectedSectionId: string
): LineSection[] {
    return sections.map(section => (section.id === selectedSectionId ? { ...section, lines } : section));
}

/**
 * Toggles the showDividerAfter property for a specific line within a section.
 *
 * @param sections - The current list of sections
 * @param sectionId - The ID of the section containing the line
 * @param lineId - The ID of the line to toggle
 * @returns The updated sections array with the divider toggled
 */
export function toggleLineDivider(sections: LineSection[], sectionId: string, lineId: string): LineSection[] {
    return sections.map(section => {
        if (section.id === sectionId) {
            return {
                ...section,
                lines: section.lines.map(line =>
                    line.id === lineId ? { ...line, showDividerAfter: !line.showDividerAfter } : line
                ),
            };
        }

        return section;
    });
}

/**
 * Generates a new temporary ID for an unsaved section.
 * Temporary IDs are negative numbers to distinguish them from saved sections.
 * Numbering starts at -1 and decrements for each new section.
 *
 * @param sections - The current list of sections
 * @returns A new negative ID that doesn't conflict with existing temporary IDs
 */
export function generateTempLineSectionId(sections: LineSection[]): string {
    const sectionIds = sections.map(section => parseSectionId(section.id));
    const negativeIds = sectionIds.filter(id => id < 0);

    if (negativeIds.length === 0) {
        return toSectionId(-1);
    }

    const minId = Math.min(...negativeIds);
    return toSectionId(minId - 1);
}

/**
 * Maps local LineSection[] to API ILineGroupGroup[] format.
 * Converts negative IDs (temp IDs for new sections) to null.
 * Adds sort_order based on array position.
 */
export function mapLineSectionsToApi(sections: LineSection[]): ILineGroupGroup[] {
    return sections.map((section, sectionIndex) => {
        const sectionId = parseSectionId(section.id);
        return {
            id: isTempSectionId(section.id) ? null : sectionId,
            title: section.title,
            sort_order: sectionIndex,
            lines: section.lines.map((line, lineIndex) => ({
                line_id: parseLineId(line.id),
                show_divider_after: line.showDividerAfter,
                sort_order: lineIndex,
            })),
        };
    });
}

/**
 * Maps API ILineGroupGroup[] response back to local LineSection[] format.
 */
export function mapLineSectionsFromApi(apiGroups: ILineGroupGroup[]): LineSection[] {
    return apiGroups
        .sort((a, b) => a.sort_order - b.sort_order)
        .map(group => ({
            id: toSectionId(group.id ?? -1),
            title: group.title,
            lines: group.lines
                .sort((a, b) => a.sort_order - b.sort_order)
                .map(line => ({
                    id: toLineId(line.line_id),
                    title: "", // title is empty for now, will be set when lines are fetched
                    showDividerAfter: line.show_divider_after,
                })),
        }));
}

/**
 * Adds titles to sections and creates the default section.
 * @param sections - The sections to add titles to
 * @param lines - The lines to add titles from
 * @returns The sections with titles and the default section
 */
export function getSectionsViewModel(sections: LineSection[], lines: IShiftPersonLineRec[]): LineSection[] {
    const lineSectionsVm = addTitlesToSections(sections, lines);
    const defaultSection = createDefaultLineSection(sections, lines);
    return [...lineSectionsVm, defaultSection];
}

/**
 * Adds titles to sections.
 * @param sections - The sections to add titles to
 * @param lines - The lines to add titles from
 * @returns The sections with titles
 */
function addTitlesToSections(sections: LineSection[], lines: IShiftPersonLineRec[]): LineSection[] {
    const lineTitlesById = new Map(lines.map(line => [toLineId(line.line_id), line.line_title]));
    return [
        ...sections.map(section => {
            const linesWithTitles = section.lines.map(line => ({ ...line, title: lineTitlesById.get(line.id) ?? "" }));
            return {
                ...section,
                lines: linesWithTitles,
            };
        }),
    ];
}

/**
 * Stores sections for a line group to the database.
 * Handles temporary negative IDs by converting them to null (new sections).
 *
 * @param lineGroupUuid - The UUID of the line group to store sections for
 * @param sections - The sections to store (may contain negative IDs for new sections)
 * @returns The stored sections with all IDs being positive (new IDs assigned by the backend)
 */
export async function storeLineSections(lineGroupUuid: string, sections: LineSection[]): Promise<LineSection[]> {
    const lineGroup = getLineGroupForUser(lineGroupUuid);
    if (lineGroup === null) {
        throw new Error(`Line group not found: ${lineGroupUuid}`);
    }

    // Map local format to API format (negative IDs become null)
    const apiGroups = mapLineSectionsToApi(sections);

    // Save to database
    const response = await getBackend().manufacturing.setLineGroupGroups({
        linegroup_id: lineGroup.id,
        groups: apiGroups,
    });

    // Map response back to local format (with new IDs assigned)
    return mapLineSectionsFromApi(response.groups);
}

/**
 * Fetches sections for a line group from the database.
 *
 * @param lineGroupUuid - The UUID of the line group to fetch sections for
 * @returns The sections with their ordered lines, or empty array if line group not found
 */
export async function fetchLineSections(lineGroupUuid: string): Promise<LineSection[]> {
    const lineGroup = getLineGroupForUser(lineGroupUuid);
    if (lineGroup === null) {
        throw new Error(`Line group not found: ${lineGroupUuid}`);
    }

    const { groups } = await getBackend().manufacturing.getLineGroupGroups({
        linegroup_id: lineGroup.id,
    });

    return mapLineSectionsFromApi(groups);
}

/**
 * Arranges lines into sections for rendering.
 * Lines that are not in any section are added to the end of the array as "Other".
 * @param lines - Array of line records.
 * @param sections - Array of line sections.
 * @param getLineId - Function to extract the ID from a line.
 * @returns Array of sectioned lines. `sectionHeader` is only defined for the first
 * line in each section. `showDividerAfter` is always `false` for the last line in each section.
 */
export function getSectionedLines<T>(
    lines: T[],
    sections: LineSection[],
    getLineId: (line: T) => string | number
): SectionedLine<T>[] {
    const linesById = new Map(lines.map(line => [getLineId(line), line]));
    const result = new Map<string | number, SectionedLine<T>>();

    // Arrange lines based on sections
    for (const section of sections) {
        let lastLineInSection: SectionedLine<T> | null = null;
        let isFirstLineInSection = true;

        for (const sectionLine of section.lines) {
            const line = linesById.get(sectionLine.id);
            if (!line) {
                continue;
            }

            const sectionedLine: SectionedLine<T> = {
                sectionHeader: isFirstLineInSection
                    ? {
                          id: section.id,
                          title: section.title,
                      }
                    : null,
                line,
                showDividerAfter: sectionLine.showDividerAfter,
            };

            result.set(sectionLine.id, sectionedLine);
            lastLineInSection = sectionedLine;
            isFirstLineInSection = false;
        }

        // Remove divider after last line in section
        if (lastLineInSection) {
            lastLineInSection.showDividerAfter = false;
        }
    }

    // Add unsectioned lines to default section if at least one line was sectioned.
    let isFirstUnsectionedLine = result.size > 0;

    for (const line of linesById.values()) {
        if (result.has(getLineId(line))) {
            continue;
        }

        result.set(getLineId(line), {
            sectionHeader: isFirstUnsectionedLine
                ? {
                      id: DEFAULT_LINE_SECTION_ID,
                      title: getDefaultLineSectionTitle(),
                  }
                : null,
            line,
            showDividerAfter: false,
        });
        isFirstUnsectionedLine = false;
    }

    return Array.from(result.values());
}
